import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import DataApi from '../../../api/DataApi';
import WatconnectApi from '../../../api/WatconnectApi';
import './WatConnectChat.css';
import FileAttachmentModal from '../whatsapp_messenger/FileAttachmentModal';
import TemplateModal from '../whatsapp_messenger/TemplateModal';
// import * as lamejs from 'lamejs'; // Temporarily disabled due to compatibility issues

const WatConnectChat = ({
  socket,
  selectedWhatsAppSetting,
  isOpen = false,
  onToggle,
  recipientName = '',
  recipientPhone = '',
  onClose,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRef = useRef(null);
  const chatBodyRef = useRef(null);
  const [showFileAttachment, setShowFileAttachment] = useState(false);
  useEffect(() => {
    if (!socket) return;

    socket.on('receivedwhatsappmessage', data => {
      const socketNumber = (
        data?.recipient_id ||
        data?.to ||
        data?.from ||
        ''
      ).replace(/\D/g, '');
      const currentNumber = recipientPhone.replace(/\D/g, '');

      if (socketNumber === currentNumber) {
        getMessageHistoryRecords(selectedWhatsAppSetting);
      }
    });

    return () => socket.off('receivedwhatsappmessage');
  }, [socket, recipientPhone, selectedWhatsAppSetting]);

  // ✅ 2️⃣ Fetch history — only when user opens chat or changes WhatsApp setting
  useEffect(() => {
    if (recipientPhone && selectedWhatsAppSetting) {
      getMessageHistoryRecords(selectedWhatsAppSetting);
    }
  }, [recipientPhone, selectedWhatsAppSetting]);

  const handleFileSendComplete = () => {
    // Refresh messages after file send
    getMessageHistoryRecords(selectedWhatsAppSetting);
  };

  const extractParameters = text => {
    if (!text) return [];
    const paramRegex = /\{\{(\d+)\}\}/g;
    const params = [];
    let match;
    while ((match = paramRegex.exec(text)) !== null) {
      params.push(parseInt(match[1]));
    }
    return [...new Set(params)].sort((a, b) => a - b);
  };

  const handleTemplateSend = async submittedData => {
    const log = (msg, data = null) => console.log(`🔹 ${msg}`, data ?? '');
    const logError = (msg, err) =>
      console.error(`❌ ${msg}`, err?.response?.data || err);

    // Utility: fail-fast if condition is false
    const assertSuccess = (condition, message) => {
      if (!condition) throw new Error(message);
    };

    const { selectedTemplate, selectedWhatsAppSetting, parameterValues } =
      submittedData;

    try {
      log('🚀 Sending Template Started');
      const watconnectApi = new WatconnectApi('webhook_template');
      const historyApi = new DataApi('watconnect');

      log('📦 Selected Template:', selectedTemplate);
      log('📱 Recipient:', recipientPhone, recipientName);
      log('⚙️ Selected WhatsApp Setting:', selectedWhatsAppSetting);

      // ========== STEP 1: Upload Media ==========
      const mediaUploads = {};
      const headerComponents =
        selectedTemplate.components?.filter(comp => comp.type === 'HEADER') ||
        [];

      for (const component of headerComponents) {
        if (
          component.format === 'IMAGE' ||
          component.format === 'VIDEO' ||
          component.format === 'DOCUMENT'
        ) {
          try {
            log(`📄 Getting ${component.format} Header from existing file`);

            const fileUrl = selectedTemplate.file_data?.file_url;
            assertSuccess(
              fileUrl,
              `No file URL for ${component.format} header`
            );

            log(`📁 Using existing file: ${fileUrl}`);
            const requestbody = {
              header: component.format,
              header_body: fileUrl,
            };

            const uploadResponse = await watconnectApi.post(
              `/upload_whatsapp_media?whatsapp_setting_number=${encodeURIComponent(
                selectedWhatsAppSetting
              )}`,
              requestbody
            );

            log('📤 Media ID Response:', uploadResponse);

            const mediaId =
              uploadResponse?.data?.id ||
              uploadResponse?.id ||
              uploadResponse?.documentId ||
              uploadResponse?.data?.documentId;

            assertSuccess(
              mediaId,
              `Failed to get media ID for ${component.format} header`
            );

            mediaUploads[`header_${component.format}`] = mediaId;
            log(`✅ Got media ID for ${component.format} header: ${mediaId}`);
          } catch (err) {
            logError(`${component.format} Header Media ID Failed`, err);
            alert(`❌ Media upload failed `);
            setShowTemplateModal(false);
            return; // ⛔ Stop further execution
          }
        }
      }

      // ========== STEP 2: Build WhatsApp Payload ==========
      log('🧩 Building Template Payload Components');

      const templatePayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'template',
        template: {
          name: selectedTemplate.name,
          language: {
            code: selectedTemplate.language || 'en',
            policy: 'deterministic',
          },
          components: [],
        },
      };

      const components = [];

      // HEADER
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'HEADER') {
          const headerComponent = { type: 'header', parameters: [] };
          if (
            component.format === 'IMAGE' ||
            component.format === 'VIDEO' ||
            component.format === 'DOCUMENT'
          ) {
            const mediaId = mediaUploads[`header_${component.format}`];
            if (mediaId) {
              if (
                component.format === 'IMAGE' ||
                component.format === 'VIDEO'
              ) {
                headerComponent.parameters.push({
                  type: component.format.toLowerCase(),
                  [component.format.toLowerCase()]: { id: mediaId },
                });
              } else if (component.format === 'DOCUMENT') {
                headerComponent.parameters.push({
                  type: 'document',
                  document: {
                    id: mediaId,
                    filename:
                      selectedTemplate.file_data?.file_name || 'document.pdf',
                  },
                });
              }
            }
          }
          if (headerComponent.parameters.length > 0)
            components.push(headerComponent);
        }
      });

      // BODY
      selectedTemplate.components?.forEach(component => {
        if (component.type === 'BODY') {
          const bodyComponent = { type: 'body', parameters: [] };
          const params = extractParameters(component.text);
          params.forEach(param => {
            const value = parameterValues[`body_${param}`];
            if (value)
              bodyComponent.parameters.push({ type: 'text', text: value });
          });
          if (bodyComponent.parameters.length > 0)
            components.push(bodyComponent);
        }
      });

      templatePayload.template.components = components;

      log(
        '📨 Final Template Payload:',
        JSON.stringify(templatePayload, null, 2)
      );

      // ========== STEP 3: Send WhatsApp Message ==========
      let messageResponse;
      try {
        log('📤 Sending WhatsApp Template Message...');
        messageResponse = await watconnectApi.post(
          `/message?whatsapp_setting_number=${encodeURIComponent(
            selectedWhatsAppSetting
          )}`,
          templatePayload
        );
        log('📬 Message Response:', messageResponse);
        assertSuccess(
          messageResponse?.messages?.[0]?.id,
          'Failed to send WhatsApp message'
        );
      } catch (err) {
        logError('Message Send Failed', err);
        alert(
          `❌ Failed to send WhatsApp message: ${
            err.response?.data?.message || err.message
          }`
        );
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 4: Create History Record ==========
      let historyRecord;
      try {
        const historyPayload = {
          name: recipientName || '',
          template_name: selectedTemplate.name || '',
          whatsapp_number: recipientPhone.replace(/^\+/, ''),
          message: ``,
          status: 'Outgoing',
          business_number: selectedWhatsAppSetting,
          delivery_status: 'sent',
          message_id: messageResponse?.messages?.[0]?.id,
        };
        log('🗂 Creating History Record:', historyPayload);
        const historyRes = await historyApi.post(`/history`, historyPayload);
        log('🗄 History Response:', historyRes);
        historyRecord = historyRes?.data?.data || historyRes;
        assertSuccess(historyRecord?.id, 'Failed to create message history');
      } catch (err) {
        logError('History Creation Failed', err);
        alert('❌ Failed to create message history.');
        setShowTemplateModal(false);
        return; // ⛔ Stop further execution
      }

      // ========== STEP 5: Update Parameters in History ==========
      try {
        const parametersData = {};
        const mainParams = {};

        Object.keys(parameterValues).forEach(key => {
          if (key.startsWith('body_')) {
            const paramNumber = key.split('_')[1];
            const value = parameterValues[key];
            if (value) mainParams[paramNumber] = value;
          }
        });

        if (selectedTemplate.file_data) {
          mainParams.file_url = selectedTemplate.file_data.file_url;
          mainParams.file_type = selectedTemplate.file_data.file_type;
          mainParams.file_name = selectedTemplate.file_data.file_name;
          mainParams.mime_type = selectedTemplate.file_data.mime_type;
        }

        if (Object.keys(mainParams).length > 0)
          parametersData.main = mainParams;

        log('🧩 Updating History Parameters:', parametersData);

        await historyApi.post(`/history`, {
          id: historyRecord.id,
          parameters: parametersData,
        });
      } catch (err) {
        logError('History Parameter Update Failed', err);
        // Note: not a fatal step, history already exists
      }

      log('🎉 Template Sending Completed Successfully!');
    } catch (err) {
      logError('Unhandled Template Send Error', err);
      alert(
        'Failed to send template: ' +
          (err.response?.data?.message || err.message)
      );
    }

    setShowTemplateModal(false);
    getMessageHistoryRecords(selectedWhatsAppSetting);
  };
  const getMessageHistoryRecords = async selectedWhatsAppSetting => {
    const dataApi = new DataApi('watconnect_history');

    if (recipientPhone && selectedWhatsAppSetting) {
      try {
        let result = await dataApi.fetchById(
          `records/${recipientPhone}?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`
        );

        if (result.data?.success) {
          const formatted = (result.data?.records || []).map(r => ({
            ...r.history,
            template: r.template,
            params: r.params,
          }));
          console.log(formatted);
          setChatMessages(formatted);
        } else {
          setChatMessages([]);
        }
      } catch (error) {
        console.error('Error fetching chat history:', error);
        setChatMessages([]);
      }
    } else {
      setChatMessages([]);
    }
  };

  const getFileTypeFromUrl = fileUrl => {
    if (!fileUrl) return 'document';

    const extension = fileUrl.split('.').pop().toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];

    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (audioExtensions.includes(extension)) return 'audio';

    return 'document';
  };

  // Helper function to convert file path to URL
  const convertFilePathToUrl = filePath => {
    if (!filePath) return '';

    // If it's already a URL, return as is
    if (filePath.startsWith('http')) return filePath;

    // Convert local file path to web URL
    // You might need to adjust this based on your server configuration
    if (filePath.includes('public/demo/attachment')) {
      // Extract the filename and construct URL
      const filename = filePath.split('/').pop();
      return `https://admin.watconnect.com/public/demo/attachment/${filename}`;
    }

    // For other file paths, you might need a different conversion logic
    return filePath;
  };

  // Fetch WhatsApp chat history
  // const fetchChatHistory = async () => {
  //   if (!recipientPhone) {
  //     console.log('No recipient phone number provided');
  //     // Load dummy messages if no phone
  //     setChatMessages([
  //       {
  //         id: 1,
  //         type: 'received',
  //         content: 'Hello! How can I help you today?',
  //         timestamp: '10:30 AM',
  //       },
  //       {
  //         id: 2,
  //         type: 'sent',
  //         content: 'Hi, I need some information about your services.',
  //         timestamp: '10:32 AM',
  //       },
  //       {
  //         id: 3,
  //         type: 'sent',
  //         messageType: 'image',
  //         content: 'product-image.jpg',
  //         caption: 'Can you tell me about this product?',
  //         timestamp: '10:34 AM',
  //       },
  //       {
  //         id: 4,
  //         type: 'received',
  //         messageType: 'file',
  //         content: 'product-catalog.pdf',
  //         fileSize: '2.4 MB',
  //         timestamp: '10:35 AM',
  //       },
  //       {
  //         id: 5,
  //         type: 'sent',
  //         messageType: 'voice',
  //         duration: '0:15',
  //         timestamp: '10:36 AM',
  //       },
  //     ]);
  //     return;
  //   }

  //   setIsLoadingHistory(true);
  //   try {
  //     const dataApi = new DataApi('object/list/watconnect_history');
  //     // Fetch WhatsApp messages filtered by whatsapp_number field
  //     const response = await dataApi.fetchAll(
  //       `filter1=whatsapp_number:=:${recipientPhone}&sortBy=createddate&sortOrder=asc`
  //     );

  //     console.log('WhatsApp history response:', response);
  //     console.log('Response data:', response.data);
  //     console.log('Response data.data:', response.data?.data);

  //     // The data is in response.data.data (array), not response.data.records
  //     const records = response.data?.data || [];

  //     if (records.length > 0) {
  //       // Transform the records to chat message format and fetch attachments
  //       const messages = await Promise.all(
  //         records.map(async (record, index) => {
  //           const messageId = record.id?.value || index;

  //           // Fetch attachments for this message
  //           let attachments = [];
  //           try {
  //             const attachmentApi = new DataApi('object/list/attachment');
  //             const attachmentResponse = await attachmentApi.fetchAll(
  //               `filter1=parent_id:=:${messageId}`
  //             );
  //             attachments = attachmentResponse.data?.data || [];
  //             console.log(`Attachments for message ${messageId}:`, attachments);
  //           } catch (error) {
  //             console.warn(
  //               `Failed to fetch attachments for message ${messageId}:`,
  //               error
  //             );
  //           }

  //           // Determine message type based on attachments or message content
  //           let messageType = null;
  //           let localFile = null;

  //           if (attachments.length > 0) {
  //             const attachment = attachments[0]; // Use first attachment
  //             const fileName = attachment.file_name?.value || '';
  //             const mimeType = attachment.mime_type?.value || '';

  //             // Determine message type based on MIME type
  //             if (mimeType.startsWith('image/')) {
  //               messageType = 'image';
  //             } else if (mimeType.startsWith('video/')) {
  //               messageType = 'video';
  //             } else if (mimeType.startsWith('audio/')) {
  //               messageType = 'audio';
  //             } else {
  //               messageType = 'document';
  //             }

  //             // Create localFile object for UI
  //             // Convert local file path to web URL
  //             const fileUrl = attachment.file_url?.value;
  //             const webUrl = fileUrl
  //               ? fileUrl.replace(
  //                   '/Users/aslambari/projects/ibs-spark/ibs-spark-backend',
  //                   ''
  //                 )
  //               : null;

  //             localFile = {
  //               id: attachment.id?.value,
  //               name: attachment.file_name?.value,
  //               url: webUrl,
  //               type: attachment.file_type?.value,
  //               size: attachment.file_size?.value,
  //             };
  //           }

  //           return {
  //             id: messageId,
  //             type:
  //               record.status?.value?.toLowerCase() === 'outgoing'
  //                 ? 'sent'
  //                 : 'received',
  //             content: record.message?.value || '',
  //             messageType: messageType,
  //             templateName:
  //               record.template_name?.displayValue ||
  //               record.template_name?.value ||
  //               null,
  //             deliveryStatus:
  //               record.delivery_status?.value?.toLowerCase() || null,
  //             isRead: record.is_read?.value || false,
  //             messageId: record.message_id?.value || null,
  //             timestamp: record.createddate?.value
  //               ? new Date(record.createddate.value).toLocaleTimeString(
  //                   'en-US',
  //                   { hour: 'numeric', minute: '2-digit' }
  //                 )
  //               : '',
  //             localFile: localFile,
  //             attachments: attachments,
  //           };
  //         })
  //       );

  //       setChatMessages(messages);
  //       console.log('Transformed messages with attachments:', messages);
  //     } else {
  //       // No messages found, show empty state
  //       setChatMessages([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching WhatsApp history:', error);
  //     // On error, show dummy messages
  //     setChatMessages([
  //       {
  //         id: 1,
  //         type: 'received',
  //         content: 'Error loading chat history. Showing sample messages.',
  //         timestamp: new Date().toLocaleTimeString('en-US', {
  //           hour: 'numeric',
  //           minute: '2-digit',
  //         }),
  //       },
  //     ]);
  //   } finally {
  //     setIsLoadingHistory(false);
  //   }
  // };

  // Fetch chat history when component opens or phone changes
  useEffect(() => {
    if (isOpen && !isMinimized) {
      getMessageHistoryRecords(selectedWhatsAppSetting);
    }
  }, [isOpen, recipientPhone]);
  const formatTime = dateString => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getDateLabel = dateString => {
    const date = new Date(dateString);
    const today = new Date();

    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current && chatMessages.length > 0) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatMessages]);


  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Check microphone permissions and provide guidance
  const checkMicrophonePermissions = async () => {
    try {
      if (!navigator.permissions) {
        console.log('Permissions API not supported');
        return;
      }

      const permissionStatus = await navigator.permissions.query({
        name: 'microphone',
      });
      console.log('Current microphone permission:', permissionStatus.state);

      if (permissionStatus.state === 'denied') {
        alert(
          'Microphone permission is denied. Please:\n1. Click the microphone icon in your browser address bar\n2. Select "Allow" for microphone access\n3. Refresh the page and try again'
        );
        return false;
      } else if (permissionStatus.state === 'prompt') {
        console.log(
          'Microphone permission will be requested when recording starts'
        );
        return true;
      } else if (permissionStatus.state === 'granted') {
        console.log('Microphone permission is granted');
        return true;
      }
    } catch (error) {
      console.log('Could not check microphone permissions:', error);
      return true; // Assume permission is available if we can't check
    }
  };

  // WhatsApp supported media formats
  const whatsappFormats = {
    audio: {
      'audio/aac': '.aac',
      'audio/amr': '.amr',
      'audio/mpeg': '.mp3',
      'audio/mp4': '.m4a',
      'audio/ogg': '.ogg',
    },
    image: {
      'image/jpeg': '.jpeg',
      'image/png': '.png',
    },
    document: {
      'text/plain': '.txt',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        '.xlsx',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        '.docx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        '.pptx',
      'application/pdf': '.pdf',
    },
    video: {
      'video/3gpp': '.3gp',
      'video/mp4': '.mp4',
    },
  };

  // Validate file type for WhatsApp compatibility
  const validateWhatsAppFile = (file, type) => {
    const supportedTypes = whatsappFormats[type];
    if (!supportedTypes) return false;

    const fileType = file.type;
    const isValidType = supportedTypes[fileType];

    if (!isValidType) {
      const supportedExtensions = Object.values(supportedTypes).join(', ');
      throw new Error(
        `Unsupported ${type} format. Supported formats: ${supportedExtensions}`
      );
    }

    // Check file size limits
    const sizeLimits = {
      audio: 16 * 1024 * 1024, // 16MB
      image: 5 * 1024 * 1024, // 5MB
      document: 100 * 1024 * 1024, // 100MB
      video: 16 * 1024 * 1024, // 16MB
    };

    if (file.size > sizeLimits[type]) {
      const maxSize = sizeLimits[type] / (1024 * 1024);
      throw new Error(
        `File size too large. Maximum size for ${type}: ${maxSize}MB`
      );
    }

    return true;
  };

  // Helper function to get emoji for message type
  const getMessageTypeEmoji = messageType => {
    const emojiMap = {
      image: '🖼️',
      video: '🎥',
      audio: '🎵',
      document: '📄',
      voice: '🎤',
    };
    return emojiMap[messageType] || '📎';
  };
  // Centralized method to create WatConnect history record
  const createWatConnectHistory = async (
    messageType,
    messageText,
    caption = null
  ) => {
    // const selectedWhatsAppSetting = '919461488615'; // You can make this dynamic

    const messageData = {
      whatsapp_number: recipientPhone,
      message: messageText, // Using 'message' field from database schema
      status: 'outgoing', // Only 'incoming' or 'outgoing' are valid values
      business_number: selectedWhatsAppSetting, // Using 'business_number' field from database schema
      delivery_status: 'sending',
      is_read: false,
      // Note: message_type, direction, media_type, caption are not in the database schema
      // We'll store these in the 'message' field or handle them differently
    };

    console.log('Creating WatConnect history record:', messageData);
    const historyApi = new DataApi('object');
    const historyResponse = await historyApi.post(
      '/watconnect_history',
      messageData
    );

    if (!historyResponse.data || !historyResponse.data.success) {
      throw new Error('Failed to create WatConnect history record');
    }

    const watconnectHistoryId = historyResponse.data.data.id;
    console.log('WatConnect history created with ID:', watconnectHistoryId);

    return {
      historyId: watconnectHistoryId,
      historyApi: historyApi,
    };
  };

  // Helper function to upload media file locally with WatConnect history ID as parent
  const uploadMediaFile = async (file, messageType, watconnectHistoryId) => {
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append(
        'description',
        `${messageType} file for WatConnect message`
      );

      console.log(
        `Uploading ${messageType} file locally with history ID:`,
        watconnectHistoryId
      );

      // Upload to local file system using DataApi with WatConnect history ID as parent
      const dataApi = new DataApi('attachments');
      const response = await dataApi.post(`/${watconnectHistoryId}`, formData);

      if (response.data && response.data.success) {
        const uploadedFile = response.data.data[0]; // Get the first uploaded file
        console.log('File uploaded locally:', uploadedFile);
        return uploadedFile;
      } else {
        throw new Error(response.data?.message || 'Local upload failed');
      }
    } catch (error) {
      console.error('Error uploading file locally:', error);
      throw error;
    }
  };

  // Helper function to send media messages (files/images)
  const sendMediaMessage = async (file, messageType, caption = '') => {
    let watconnectHistoryId = null;

    try {
      const fileName = file.name;
      const fileSize = (file.size / 1024 / 1024).toFixed(2); // Convert to MB

      console.log(`Processing ${messageType}:`, fileName, fileSize, 'MB');

      // Validate file for WhatsApp compatibility
      validateWhatsAppFile(file, messageType);

      // Step 1: Create WatConnect history record first
      const messageText = caption
        ? `${getMessageTypeEmoji(messageType)} ${messageType}: ${caption}`
        : `${getMessageTypeEmoji(messageType)} ${messageType}`;
      const { historyId, historyApi } = await createWatConnectHistory(
        messageType,
        messageText,
        caption
      );
      watconnectHistoryId = historyId;

      // Add message to chat UI with loading state
      const loadingMessage = {
        id: watconnectHistoryId,
        type: 'sent',
        messageType: messageType,
        content: fileName,
        caption: caption,
        fileName: fileName,
        fileSize: fileSize,
        deliveryStatus: 'sending',
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        }),
        isRetryable: false,
        retryCount: 0,
      };

      setChatMessages(prev => [...prev, loadingMessage]);

      // Step 2: Upload file locally with history ID as parent
      const localFile = await uploadMediaFile(
        file,
        messageType,
        watconnectHistoryId
      );
      console.log('Local file uploaded:', localFile);

      // Step 3: Upload file to WhatsApp to get document ID
      const formData = new FormData();
      formData.append('messaging_product', 'whatsapp');
      formData.append('file', file);
      formData.append('description', messageType);

      const watconnectApi = new WatconnectApi('webhook_template');
      console.log(
        `${messageType} upload - API baseUrl:`,
        watconnectApi.baseUrl
      );
      console.log(
        `${messageType} upload - FormData contents:`,
        Array.from(formData.entries())
      );

      const documentResponse = await watconnectApi.post(
        `/documentId?whatsapp_setting_number=${selectedWhatsAppSetting}`,
        formData
      );

      console.log('Document upload response:', documentResponse);
      const documentId = documentResponse?.data?.id || documentResponse?.id;

      if (!documentId) {
        throw new Error('Failed to get document ID from upload response');
      }

      // Step 4: Send media message using document ID
      let whatsappPayload;

      if (messageType === 'image') {
        whatsappPayload = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: recipientPhone.startsWith('+')
            ? recipientPhone
            : `+${recipientPhone}`,
          type: 'image',
          image: {
            id: documentId,
            caption: caption || `📷 ${fileName}`,
          },
        };
      } else if (messageType === 'video') {
        whatsappPayload = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: recipientPhone.startsWith('+')
            ? recipientPhone
            : `+${recipientPhone}`,
          type: 'video',
          video: {
            id: documentId,
            caption: caption || `🎥 ${fileName}`,
          },
        };
      } else if (messageType === 'audio') {
        whatsappPayload = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: recipientPhone.startsWith('+')
            ? recipientPhone
            : `+${recipientPhone}`,
          type: 'audio',
          audio: {
            id: documentId,
          },
        };
      } else {
        whatsappPayload = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: recipientPhone.startsWith('+')
            ? recipientPhone
            : `+${recipientPhone}`,
          type: 'document',
          document: {
            id: documentId,
            filename: fileName,
            caption: caption || `📄 ${fileName}`,
          },
        };
      }

      console.log(`Sending ${messageType} via WhatsApp:`, whatsappPayload);

      // Send media message via WhatsApp API
      const whatsappResponse = await watconnectApi.post(
        `/message?whatsapp_setting_number=${selectedWhatsAppSetting}`,
        whatsappPayload
      );

      console.log(`${messageType} sent via WhatsApp:`, whatsappResponse);

      // Step 5: Update the existing WatConnect history record with WhatsApp response and local file info
      const updateData = {
        // status remains 'outgoing' (only 'incoming' or 'outgoing' are valid)
        delivery_status: 'sent',
        message_id: whatsappResponse?.messages?.[0]?.id || null,
        // Note: document_id, local_file_* fields are not in the database schema
        // We'll need to store these in a separate table or handle them differently
      };

      console.log(
        'Updating WatConnect history record with ID:',
        watconnectHistoryId,
        'Data:',
        updateData
      );

      // Update the existing history record
      const dbResponse = await historyApi.put(
        `/watconnect_history/${watconnectHistoryId}`,
        updateData
      );

      console.log(`${messageType} message updated in database:`, dbResponse);

      // Update the existing message in chat UI with success state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === watconnectHistoryId
            ? {
                ...msg,
                deliveryStatus: 'sent',
                documentId: documentId,
                isRetryable: false,
                // Local file reference for UI
                localFile: {
                  id: localFile.id,
                  name: localFile.file_name,
                  url: localFile.file_url,
                  type: localFile.file_type,
                  size: localFile.file_size,
                },
              }
            : msg
        )
      );

      return true;
    } catch (error) {
      console.error(`Error sending ${messageType}:`, error);

      // Update the message with failed state (only if we have a history ID)
      if (watconnectHistoryId) {
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === watconnectHistoryId
              ? {
                  ...msg,
                  deliveryStatus: 'failed',
                  isRetryable: true,
                  errorMessage: error.message || 'Failed to send message',
                  retryCount: (msg.retryCount || 0) + 1,
                }
              : msg
          )
        );
      }

      // Handle CORS errors specifically
      if (error.message && error.message.includes('CORS')) {
        throw new Error(
          'CORS Error: Unable to upload file. Please check your network configuration or use a proxy server.'
        );
      }

      throw error;
    }
  };

  // Handle retry for failed messages
  const handleRetryMessage = async message => {
    if (isSending) return; // Prevent retry while sending

    setIsSending(true);
    try {
      // Update message status to sending
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === message.id
            ? {
                ...msg,
                deliveryStatus: 'sending',
                isRetryable: false,
                errorMessage: null,
              }
            : msg
        )
      );

      // Retry based on message type
      if (message.messageType === 'voice') {
        // For voice messages, we need to recreate the audio blob
        // This is complex, so for now we'll just show an error
        throw new Error(
          'Voice message retry not supported. Please record again.'
        );
      } else if (message.messageType) {
        // For media messages, we need the original file
        // This is also complex without storing the original file
        throw new Error(
          'Media message retry not supported. Please re-select the file.'
        );
      } else {
        // For text messages, we can retry easily
        await retryTextMessage(message);
      }
    } catch (error) {
      console.error('Error retrying message:', error);

      // Update message with failed state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === message.id
            ? {
                ...msg,
                deliveryStatus: 'failed',
                isRetryable: true,
                errorMessage: error.message || 'Retry failed',
                retryCount: (msg.retryCount || 0) + 1,
              }
            : msg
        )
      );

      alert(`Retry failed: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Retry text message
  const retryTextMessage = async message => {
    // const selectedWhatsAppSetting = '919461488615';

    // Prepare WhatsApp API payload
    const whatsappPayload = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: recipientPhone.startsWith('+')
        ? recipientPhone
        : `+${recipientPhone}`,
      type: 'text',
      text: {
        preview_url: false,
        body: message.content,
      },
    };

    console.log('Retrying WhatsApp message:', whatsappPayload);

    // Send via WhatsApp API
    const watconnectApi = new WatconnectApi('webhook_template');
    const whatsappResponse = await watconnectApi.post(
      `/message?whatsapp_setting_number=${selectedWhatsAppSetting}`,
      whatsappPayload
    );

    console.log('WhatsApp message retry successful:', whatsappResponse);

    // Update the existing WatConnect history record with WhatsApp response
    const updateData = {
      // status remains 'outgoing' (only 'incoming' or 'outgoing' are valid)
      delivery_status: 'sent',
      message_id: whatsappResponse?.messages?.[0]?.id || null,
    };

    console.log(
      'Updating WatConnect history record with ID:',
      message.id,
      'Data:',
      updateData
    );

    const historyApi = new DataApi('object');
    const dbResponse = await historyApi.put(
      `/watconnect_history/${message.id}`,
      updateData
    );

    console.log('Text message retry updated in database:', dbResponse);

    // Update the existing message in chat UI with success state
    setChatMessages(prev =>
      prev.map(msg =>
        msg.id === message.id
          ? {
              ...msg,
              deliveryStatus: 'sent',
              isRetryable: false,
              errorMessage: null,
            }
          : msg
      )
    );
  };


  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      // 1️⃣ Prepare WhatsApp payload
      const whatsappPayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'text',
        text: {
          preview_url: false,
          body: message.trim(),
        },
      };

      // 2️⃣ Send message via WhatsApp API
      const watconnectApi = new WatconnectApi('webhook_template');
      const whatsappResponse = await watconnectApi.post(
        `/message?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting || '')}`,
        whatsappPayload
      );

      // 3️⃣ Prepare DB update payload for WatConnect history
      const updateData = {
        name: recipientName || '',
        whatsapp_number: recipientPhone.replace(/^\+/, ''),
        message: message.trim(),
        status: 'Outgoing',
        business_number: selectedWhatsAppSetting,
        delivery_status: 'sent',
        message_id: whatsappResponse?.messages?.[0]?.id || null,
      };

      // 4️⃣ Save to DB
      const historyApi = new DataApi('watconnect');
      await historyApi.post(`/history`, updateData);

      // 5️⃣ Update UI instantly (no need to wait for API refetch)
      const msgObj = {
        id: Date.now().toString(),
        message: message.trim(),
        status: 'Outgoing',
        delivery_status: 'sent',
        is_read: false,
        created_at: new Date().toISOString(),
        template: null,
        params: null,
      };

      setChatMessages(prev => [...prev, msgObj]);
      setMessage('');
    } catch (error) {
      console.error('❌ Error sending WhatsApp message:', error);
    }
  };


  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate file for WhatsApp compatibility (only documents now)
        validateWhatsAppFile(file, 'document');

        setSelectedFile(file);
        setSelectedImage(null); // Clear image if file is selected
        setSelectedVideo(null); // Clear video if file is selected
        setSelectedAudio(null); // Clear audio if file is selected
        console.log(
          'File selected:',
          file.name,
          file.type,
          (file.size / 1024 / 1024).toFixed(2) + 'MB'
        );
      } catch (error) {
        alert(error.message);
        e.target.value = ''; // Clear the input
      }
    }
  };

  const handleImageSelect = e => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate image for WhatsApp compatibility
        validateWhatsAppFile(file, 'image');

        setSelectedImage(file);
        setSelectedFile(null); // Clear file if image is selected
        setSelectedVideo(null); // Clear video if image is selected
        setSelectedAudio(null); // Clear audio if image is selected
        console.log(
          'Image selected:',
          file.name,
          file.type,
          (file.size / 1024 / 1024).toFixed(2) + 'MB'
        );
      } catch (error) {
        alert(error.message);
        e.target.value = ''; // Clear the input
      }
    }
  };

  const handleVideoSelect = e => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate video for WhatsApp compatibility
        validateWhatsAppFile(file, 'video');

        setSelectedVideo(file);
        setSelectedFile(null); // Clear file if video is selected
        setSelectedImage(null); // Clear image if video is selected
        setSelectedAudio(null); // Clear audio if video is selected
        console.log(
          'Video selected:',
          file.name,
          file.type,
          (file.size / 1024 / 1024).toFixed(2) + 'MB'
        );
      } catch (error) {
        alert(error.message);
        e.target.value = ''; // Clear the input
      }
    }
  };

  const handleAudioSelect = e => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate audio for WhatsApp compatibility
        validateWhatsAppFile(file, 'audio');

        setSelectedAudio(file);
        setSelectedFile(null); // Clear file if audio is selected
        setSelectedImage(null); // Clear image if audio is selected
        setSelectedVideo(null); // Clear video if audio is selected
        console.log(
          'Audio selected:',
          file.name,
          file.type,
          (file.size / 1024 / 1024).toFixed(2) + 'MB'
        );
      } catch (error) {
        alert(error.message);
        e.target.value = ''; // Clear the input
      }
    }
  };


  const handleRemoveAttachment = () => {
    setSelectedFile(null);
    setSelectedImage(null);
    setSelectedVideo(null);
    setSelectedAudio(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (audioInputRef.current) audioInputRef.current.value = '';
  };

  // Voice recording handlers
  const startRecording = async () => {
    try {
      console.log('Starting recording...');

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }

      // Check microphone permissions first
      const permissionStatus = await navigator.permissions.query({
        name: 'microphone',
      });
      console.log('Microphone permission status:', permissionStatus.state);

      if (permissionStatus.state === 'denied') {
        throw new Error(
          'Microphone permission denied. Please enable microphone access in your browser settings.'
        );
      }

      // Try with basic constraints first, then fallback to advanced
      let stream;
      try {
        // Use audio constraints optimized for WhatsApp (mono, lower bitrate)
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1, // Mono audio for WhatsApp compatibility
            sampleRate: 16000, // Lower sample rate for smaller file size
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        console.log('Audio stream obtained with advanced constraints');
      } catch (advancedError) {
        console.warn(
          'Advanced audio constraints failed, trying basic constraints:',
          advancedError
        );
        // Fallback to basic audio constraints
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('Audio stream obtained with basic constraints');
      }

      // Check if MediaRecorder supports WhatsApp-compatible formats
      let mediaRecorder;
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/mp4',
        });
        console.log('Using MP4 audio format (WhatsApp compatible)');
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/ogg',
        });
        console.log('Using OGG audio format (WhatsApp compatible)');
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm',
        });
        console.log('Using WebM audio format (fallback)');
      } else {
        // Fallback to default format
        mediaRecorder = new MediaRecorder(stream);
        console.log('Using default MediaRecorder format');
      }

      mediaRecorderRef.current = mediaRecorder;

      const audioChunks = [];

      mediaRecorder.ondataavailable = event => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        // Use the same format as the MediaRecorder
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunks, { type: mimeType });
        console.log(
          'Audio blob created:',
          audioBlob.type,
          audioBlob.size,
          'bytes'
        );
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      console.log('Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);

      // Provide specific error messages based on the error type
      let errorMessage = 'Unable to access microphone. ';

      if (error.name === 'NotAllowedError') {
        errorMessage += 'Please grant microphone permission and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'No microphone found. Please connect a microphone.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage += 'Microphone access is not supported in this browser.';
      } else if (error.name === 'NotReadableError') {
        errorMessage += 'Microphone is being used by another application.';
      } else if (error.message.includes('permission denied')) {
        errorMessage +=
          'Microphone permission denied. Please enable microphone access in your browser settings.';
      } else {
        errorMessage += `Error: ${error.message}`;
      }

      alert(errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      console.log('Recording stopped');
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      setAudioBlob(null);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      console.log('Recording cancelled');
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setRecordingDuration(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob) return;
    if (isSending) return;

    setIsSending(true);
    let watconnectHistoryId = null;

    try {
      const selectedWhatsAppSetting = '919461488615'; // You can make this dynamic

      console.log('Using original audio format for voice message');
      console.log(
        'Audio blob details:',
        audioBlob.type,
        audioBlob.size,
        'bytes'
      );

      // Use original audio format directly (MP4 audio is WhatsApp compatible)
      const mp3Blob = audioBlob; // Use original blob
      const voiceFileName = `voice_message_${Date.now()}.m4a`; // Use .m4a extension for MP4 audio
      const voiceMessageText = `🎤 Voice message (${formatDuration(recordingDuration)})`;

      console.log(
        'Uploading voice message:',
        voiceFileName,
        formatDuration(recordingDuration)
      );
      console.log('Audio blob details:', mp3Blob.type, mp3Blob.size, 'bytes');

      // Validate audio blob
      if (!mp3Blob || mp3Blob.size === 0) {
        throw new Error('No audio data available');
      }

      // Check audio file size (16MB limit for WhatsApp)
      const maxAudioSize = 16 * 1024 * 1024; // 16MB
      if (mp3Blob.size > maxAudioSize) {
        throw new Error('Audio file too large. Maximum size: 16MB');
      }

      // Validate audio format
      console.log('Using audio format:', mp3Blob.type);

      // Check if it's a supported WhatsApp audio format
      const supportedFormats = [
        'audio/mp4',
        'audio/mpeg',
        'audio/ogg',
        'audio/aac',
      ];
      const isSupported = supportedFormats.some(format =>
        mp3Blob.type.includes(format.split('/')[1])
      );

      if (!isSupported) {
        console.warn(
          'Audio format might not be fully compatible with WhatsApp:',
          mp3Blob.type
        );
      }

      // Create a proper File object from the audio blob
      const audioFile = new File([mp3Blob], voiceFileName, {
        type: 'audio/mp4', // Force MP4 MIME type for WhatsApp compatibility
      });

      // Step 1: Create WatConnect history record first
      const { historyId, historyApi } = await createWatConnectHistory(
        'voice',
        voiceMessageText,
        null
      );
      watconnectHistoryId = historyId;

      // Add message to chat UI with loading state
      const loadingMessage = {
        id: watconnectHistoryId,
        type: 'sent',
        messageType: 'voice',
        content: '',
        duration: formatDuration(recordingDuration),
        deliveryStatus: 'sending',
        timestamp: new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        }),
        isRetryable: false,
        retryCount: 0,
      };

      setChatMessages(prev => [...prev, loadingMessage]);

      // Step 2: Upload audio file locally with history ID as parent
      const localFile = await uploadMediaFile(
        audioFile,
        'voice',
        watconnectHistoryId
      );
      console.log('Voice file uploaded locally:', localFile);

      // Step 3: Upload audio blob to WhatsApp to get document ID
      const formData = new FormData();
      formData.append('messaging_product', 'whatsapp');
      formData.append('file', audioFile);
      formData.append('description', 'audio');

      const watconnectApi = new WatconnectApi('webhook_template');
      console.log('Voice message - API baseUrl:', watconnectApi.baseUrl);
      console.log(
        'Voice message - FormData contents:',
        Array.from(formData.entries())
      );

      const documentResponse = await watconnectApi.post(
        `/documentId?whatsapp_setting_number=${selectedWhatsAppSetting}`,
        formData
      );

      console.log('Audio upload response:', documentResponse);
      const documentId = documentResponse?.data?.id || documentResponse?.id;

      if (!documentId) {
        console.error('Document upload failed. Response:', documentResponse);
        throw new Error(
          `Failed to get document ID from audio upload response. Response: ${JSON.stringify(documentResponse)}`
        );
      }

      console.log('Document ID obtained:', documentId);

      // Step 4: Send audio message via WhatsApp API
      const whatsappPayload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: recipientPhone.startsWith('+')
          ? recipientPhone
          : `+${recipientPhone}`,
        type: 'audio',
        audio: {
          id: documentId,
        },
      };

      console.log('Sending voice message via WhatsApp:', whatsappPayload);

      // Send via WhatsApp API
      const messageApi = new WatconnectApi('webhook_template');
      const whatsappResponse = await messageApi.post(
        `/message?whatsapp_setting_number=${selectedWhatsAppSetting}`,
        whatsappPayload
      );

      console.log('Voice message sent via WhatsApp:', whatsappResponse);

      // Check if WhatsApp API call was successful
      if (
        !whatsappResponse ||
        !whatsappResponse.messages ||
        whatsappResponse.messages.length === 0
      ) {
        console.error('WhatsApp API call failed. Response:', whatsappResponse);
        throw new Error(
          `WhatsApp API call failed. Response: ${JSON.stringify(whatsappResponse)}`
        );
      }

      // Step 5: Update the existing WatConnect history record with WhatsApp response and local file info
      const updateData = {
        // status remains 'outgoing' (only 'incoming' or 'outgoing' are valid)
        delivery_status: 'sent',
        message_id: whatsappResponse?.messages?.[0]?.id || null,
        // Note: document_id, local_file_* fields are not in the database schema
        // We'll need to store these in a separate table or handle them differently
      };

      console.log(
        'Updating WatConnect history record with ID:',
        watconnectHistoryId,
        'Data:',
        updateData
      );

      // Update the existing history record
      const dbResponse = await historyApi.put(
        `/watconnect_history/${watconnectHistoryId}`,
        updateData
      );

      console.log('Voice message updated in database:', dbResponse);

      // Update the existing message in chat UI with success state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === watconnectHistoryId
            ? {
                ...msg,
                deliveryStatus: 'sent',
                documentId: documentId,
                isRetryable: false,
                // Local file reference for UI
                localFile: {
                  id: localFile.id,
                  name: localFile.file_name,
                  url: localFile.file_url,
                  type: localFile.file_type,
                  size: localFile.file_size,
                },
              }
            : msg
        )
      );

      // Clear recording
      setAudioBlob(null);
      setRecordingDuration(0);
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    } catch (error) {
      console.error('Error sending voice message:', error);

      // Update the message with failed state (only if we have a history ID)
      if (watconnectHistoryId) {
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === watconnectHistoryId
              ? {
                  ...msg,
                  deliveryStatus: 'failed',
                  isRetryable: true,
                  errorMessage: error.message || 'Failed to send voice message',
                  retryCount: (msg.retryCount || 0) + 1,
                }
              : msg
          )
        );
      }

      // Handle CORS errors specifically
      let errorMessage =
        error.message || error.response?.data?.message || 'Unknown error';
      if (error.message && error.message.includes('CORS')) {
        errorMessage =
          'CORS Error: Unable to upload voice message. Please check your network configuration or use a proxy server.';
      }

      alert(`Failed to send voice message: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioBlob) return;

    if (isPlaying) {
      // Pause
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    } else {
      // Play
      if (!audioRef.current) {
        const audio = new Audio(URL.createObjectURL(audioBlob));
        audioRef.current = audio;

        audio.onended = () => {
          setIsPlaying(false);
        };
      }

      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatDuration = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // File icons
  const pdfIcon = '/user_images/pdf-icon-png.jpg';
  const docxIcon = '/user_images/doc-icon.png';
  const xlsIcon = '/user_images/xls-icon.png';
  const textIcon = '/user_images/text-file.jpg';

  // Render message content similar to WhatsAppChat
  const renderMessageContent = msg => {
    const elements = [];

    // Check if this is a template message
    const isTemplate = msg.template && msg.template.template_name;

    if (isTemplate) {
      // ===== TEMPLATE MESSAGE RENDERING =====
      const template = msg.template;
      const params = msg.params || {};

      // 1️⃣ HEADER with Media (Top of template)
      if (
        template.header === 'IMAGE' ||
        template.header === 'VIDEO' ||
        template.header === 'DOCUMENT' ||
        template.header === 'TEXT'
      ) {
        const mainFileUrl = params.main?.file_url || params.file_url;
        const mainFileName = params.main?.file_name || params.file_name || 'Document';
        const mainFileType =
          params.main?.file_type?.toLowerCase().replace('.', '') ||
          params.file_type?.toLowerCase().replace('.', '') ||
          '';
        const mimeType = params.main?.mime_type || params.mime_type || '';

        if (template.header === 'TEXT') {
          // TEXT header - use header_body
          if (template.header_body && template.header_body !== 'null') {
            elements.push(
              <div key='template-header-text' className='template-header-text'>
                {template.header_body}
              </div>
            );
          }
        } else if (mainFileUrl) {
          // Convert file path to URL if needed
          const fileUrl = convertFilePathToUrl(mainFileUrl);

          if (template.header === 'IMAGE') {
            elements.push(
              <div key='template-header-img' className='wa-attachment'>
                <img
                  src={fileUrl}
                  alt='Header'
                  className='wa-chat-image'
                  onClick={() =>
                    setPreviewImage({
                      src: fileUrl,
                      alt: mainFileName,
                    })
                  }
                />
              </div>
            );
          } else if (template.header === 'VIDEO') {
            elements.push(
              <div key='template-header-vid' className='wa-attachment'>
                <video controls className='wa-chat-video'>
                  <source
                    src={fileUrl}
                    type={mimeType || `video/${mainFileType}`}
                  />
                </video>
              </div>
            );
          } else if (template.header === 'DOCUMENT') {
            // DOCUMENT header for template
            elements.push(
              <div key='template-header-doc' className='wa-attachment'>
                <div className='document-preview'>
                  <div className='document-icon'>📄</div>
                  <div className='document-info'>
                    <div className='document-name' title={mainFileName}>
                      {mainFileName}
                    </div>
                    <div className='document-size'>
                      {params.main?.file_size
                        ? `(${(params.main.file_size / 1024).toFixed(1)} KB)`
                        : '(Document)'}
                    </div>
                  </div>
                  <a
                    href={fileUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='document-download'
                    download
                  >
                    <i className='fas fa-download'></i>
                  </a>
                </div>
              </div>
            );
          }
        } else {
          // Fallback if no file URL
          elements.push(
            <div key='template-header-placeholder' className='wa-attachment'>
              <div className='media-preview-placeholder'>
                {template.header === 'IMAGE'
                  ? '🖼️ Image Header'
                  : template.header === 'VIDEO'
                    ? '🎥 Video Header'
                    : '📄 Document Header'}
              </div>
            </div>
          );
        }
      }

      // 2️⃣ BODY (Message content with parameters replaced)
      if (template.message_body) {
        let bodyText = template.message_body;

        // Replace {{1}}, {{2}}, etc. with actual parameter values
        if (params.main) {
          Object.keys(params.main).forEach(key => {
            if (
              key !== 'file_url' &&
              key !== 'file_type' &&
              key !== 'file_name' &&
              key !== 'mime_type' &&
              key !== 'file_size'
            ) {
              bodyText = bodyText.replace(
                new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
                params.main[key] || ''
              );
            }
          });
        }

        elements.push(
          <div key='template-body' className='wa-template-body'>
            {bodyText.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }

      // 3️⃣ FOOTER
      if (template.footer && template.footer.trim().toLowerCase() !== 'null') {
        elements.push(
          <div key='template-footer' className='wa-template-footer'>
            {template.footer}
          </div>
        );
      }

      // 4️⃣ CAROUSEL CARDS
      if (template.cards && template.cards.length > 0) {
        const carouselCards = template.cards.map((card, cardIndex) => {
          const cardParams = params.carousel?.[cardIndex] || {};

          return (
            <div key={`carousel-card-${cardIndex}`} className='carousel-card'>
              {/* Card Header Media */}
              {card.header?.format === 'IMAGE' && cardParams.file_url && (
                <div className='carousel-header'>
                  <img
                    src={convertFilePathToUrl(cardParams.file_url)}
                    alt={`Card ${cardIndex + 1}`}
                    className='wa-carousel-media'
                    onClick={() =>
                      setPreviewImage({
                        src: convertFilePathToUrl(cardParams.file_url),
                        alt: cardParams.file_name || `Card ${cardIndex + 1}`,
                      })
                    }
                  />
                </div>
              )}
              {card.header?.format === 'VIDEO' && cardParams.file_url && (
                <div className='carousel-header'>
                  <video controls className='wa-carousel-media'>
                    <source src={convertFilePathToUrl(cardParams.file_url)} />
                  </video>
                </div>
              )}

              {/* Card Body */}
              {card.body?.text && (
                <div className='carousel-body'>
                  {card.body.text.split('\n').map((line, i) => {
                    let processedLine = line;
                    // Replace parameters like {{1}}
                    Object.keys(cardParams).forEach(key => {
                      if (
                        key !== 'file_url' &&
                        key !== 'file_type' &&
                        key !== 'file_name'
                      ) {
                        processedLine = processedLine.replace(
                          new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
                          cardParams[key] || ''
                        );
                      }
                    });
                    return <div key={i}>{processedLine}</div>;
                  })}
                </div>
              )}

              {/* Card Buttons */}
              {card.buttons && card.buttons.length > 0 && (
                <div className='carousel-buttons'>
                  {card.buttons.map((btn, btnIdx) => (
                    <div key={btnIdx} className='carousel-button'>
                      {btn.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        });

        elements.push(
          <div key='carousel-wrapper' className='template-carousel'>
            <div className='carousel-container'>{carouselCards}</div>
          </div>
        );
      }
    } else {
      // ===== NON-TEMPLATE MESSAGE RENDERING =====

      // --- Media Attachments ---
      const fileUrl = msg.params?.main?.file_url || msg.params?.file_url;
      if (fileUrl) {
        let fileType =
          msg.params?.main?.file_type?.toLowerCase().replace('.', '') ||
          msg.params?.file_type?.toLowerCase().replace('.', '') ||
          '';
        const mimeType =
          msg.params?.main?.mime_type || msg.params?.mime_type || '';
        const fileName =
          msg.params?.main?.file_name || msg.params?.file_name || 'File';

        const extension = fileUrl.split('.').pop()?.toLowerCase() || '';
        if (!fileType || fileType === 'undefined' || fileType === 'null') {
          fileType = extension;
        }

        // Normalize to MIME type
        if (fileType === 'mp4') fileType = 'video/mp4';
        else if (['jpeg', 'jpg'].includes(fileType))
          fileType = 'image/jpeg';
        else if (fileType === 'png') fileType = 'image/png';
        else if (fileType === 'gif') fileType = 'image/gif';
        else if (fileType === 'pdf') fileType = 'application/pdf';
        else if (fileType === 'doc' || fileType === 'docx')
          fileType = 'application/msword';
        else if (fileType === 'xls' || fileType === 'xlsx')
          fileType = 'application/vnd.ms-excel';
        else if (fileType === 'mp3') fileType = 'audio/mpeg';
        else if (fileType === 'ogg') fileType = 'audio/ogg';
        else if (fileType === 'aac') fileType = 'audio/aac';
        else if (
          !fileType.startsWith('image/') &&
          !fileType.startsWith('video/') &&
          !fileType.startsWith('audio/') &&
          !fileType.startsWith('application/')
        ) {
          if (['jpg', 'jpeg', 'png', 'gif'].includes(extension))
            fileType = 'image/' + extension;
          else if (['mp4', 'mov', 'avi'].includes(extension))
            fileType = 'video/' + extension;
          else if (['mp3', 'ogg', 'aac'].includes(extension))
            fileType = 'audio/' + extension;
          else fileType = 'application/' + extension;
        }

        const convertedUrl = convertFilePathToUrl(fileUrl);

        // IMAGE
        if (fileType.startsWith('image/')) {
          elements.push(
            <div key='img' className='wa-attachment'>
              <a
                href={convertedUrl}
                target='_blank'
                rel='noreferrer'
                onClick={e => {
                  e.preventDefault();
                  setPreviewImage({
                    src: convertedUrl,
                    alt: fileName,
                  });
                }}
              >
                <img
                  src={convertedUrl}
                  alt='attachment'
                  className='wa-chat-image'
                />
              </a>
            </div>
          );
        }
        // VIDEO
        else if (fileType.startsWith('video/')) {
          elements.push(
            <div key='vid' className='wa-attachment'>
              <video controls className='wa-chat-video'>
                <source src={convertedUrl} type={mimeType || fileType} />
              </video>
            </div>
          );
        }
        // AUDIO
        else if (fileType.startsWith('audio/')) {
          elements.push(
            <div key='aud' className='wa-attachment'>
              <audio controls className='wa-chat-audio'>
                <source src={convertedUrl} type={mimeType || fileType} />
              </audio>
            </div>
          );
        }
        // DOCS
        else {
          elements.push(
            <div key='doc' className='wa-attachment'>
              <a
                href={convertedUrl}
                target='_blank'
                rel='noreferrer'
                download
                className='document-link'
              >
                <div className='document-preview'>
                  <div className='document-icon'>
                    {fileType.includes('pdf') ? (
                      <i className='fas fa-file-pdf'></i>
                    ) : fileType.includes('doc') ? (
                      <i className='fas fa-file-word'></i>
                    ) : fileType.includes('xls') ? (
                      <i className='fas fa-file-excel'></i>
                    ) : (
                      <i className='fas fa-file'></i>
                    )}
                  </div>
                  <div className='document-info'>
                    <div className='document-name' title={fileName}>
                      {fileName}
                    </div>
                    <div className='document-size'>
                      {msg.params?.main?.file_size
                        ? `(${(msg.params.main.file_size / 1024).toFixed(1)} KB)`
                        : '(Document)'}
                    </div>
                  </div>
                </div>
              </a>
            </div>
          );
        }
      }

      // --- Text Message ---
      if (msg.message) {
        elements.push(
          <div key='txt' className='wa-text'>
            {msg.message.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      }
    }

    // --- Buttons (for both template and non-template) ---
    const buttonsData =
      msg?.buttons || msg?.template?.temp_buttons || msg?.interactive_details;
    if (buttonsData && Array.isArray(buttonsData) && buttonsData.length > 0) {
      elements.push(
        <div key='btns' className='wa-interactive-buttons'>
          {buttonsData.map((btn, idx) => (
            <div key={idx} className='wa-interactive-button'>
              {btn.title || btn.text}
            </div>
          ))}
        </div>
      );
    }

    return <>{elements}</>;
  };

  // Convert audio blob to MP3 format (temporarily disabled due to lamejs compatibility issues)
  /*
  const convertToMP3 = async (audioBlob) => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result;
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // Get audio data
            const leftChannel = audioBuffer.getChannelData(0);
            const rightChannel = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;

            // Convert to 16-bit PCM
            const left = new Int16Array(leftChannel.length);
            const right = new Int16Array(rightChannel.length);

            for (let i = 0; i < leftChannel.length; i++) {
              left[i] = Math.max(-32768, Math.min(32767, leftChannel[i] * 32768));
              right[i] = Math.max(-32768, Math.min(32767, rightChannel[i] * 32768));
            }

            // Create MP3 encoder
            const mp3encoder = new lamejs.Mp3Encoder(1, audioBuffer.sampleRate, 128); // mono, sample rate, bitrate
            const mp3Data = [];

            // Encode in chunks
            const chunkSize = 1152; // MP3 frame size
            for (let i = 0; i < left.length; i += chunkSize) {
              const leftChunk = left.subarray(i, i + chunkSize);
              const rightChunk = right.subarray(i, i + chunkSize);
              const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
              if (mp3buf.length > 0) {
                mp3Data.push(mp3buf);
              }
            }

            // Flush remaining data
            const mp3buf = mp3encoder.flush();
            if (mp3buf.length > 0) {
              mp3Data.push(mp3buf);
            }

            // Create MP3 blob
            const mp3Blob = new Blob(mp3Data, { type: 'audio/mpeg' });
            console.log('MP3 conversion completed:', mp3Blob.size, 'bytes');
            resolve(mp3Blob);

          } catch (error) {
            console.error('Error converting to MP3:', error);
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Failed to read audio file'));
        reader.readAsArrayBuffer(audioBlob);

      } catch (error) {
        console.error('Error setting up MP3 conversion:', error);
        reject(error);
      }
    });
  };
  */

  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = event => {
      if (
        showAttachMenu &&
        !event.target.closest('.attach-menu') &&
        !event.target.closest('.attach-icon-btn')
      ) {
        setShowAttachMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachMenu]);

  // Handle ESC key to close image preview
  React.useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && previewImage) {
        setPreviewImage(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage]);

  if (!isOpen || !recipientPhone) return null;

  return (
    <>
      <div
        className={`watconnect-chat-container ${isMinimized ? 'minimized' : ''}`}
      >
        {/* Chat Header */}
        <div
          className='watconnect-chat-header'
          onClick={handleToggleMinimize}
          title={isMinimized ? 'Click to expand' : 'Click to minimize'}
        >
          <div className='header-info'>
            <div className='contact-avatar'>
              <i className='fab fa-whatsapp'></i>
            </div>
            <div className='contact-details'>
              <div className='contact-name'>{recipientName}</div>
              {recipientPhone && (
                <div className='contact-phone'>({recipientPhone})</div>
              )}
            </div>
          </div>
          <div className='header-actions'>
            <button
              className='header-btn'
              onClick={e => {
                e.stopPropagation();
                setShowTemplateModal(true);
              }}
              title='Send Template'
            >
              <i className='fa-solid fa-layer-group'></i>
            </button>
            <button
              className='header-btn'
              onClick={e => {
                e.stopPropagation();
                getMessageHistoryRecords(selectedWhatsAppSetting);
              }}
              title='Refresh chat history'
            >
              <i className='fas fa-sync-alt'></i>
            </button>
            <button
              className='header-btn header-close-btn'
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
              title='Close'
            >
              <i className='fas fa-times'></i>
            </button>
          </div>
        </div>

        {/* Chat Body (hidden when minimized) */}
        {!isMinimized && (
          <>
            {/* Chat Messages */}
            <div className='watconnect-chat-body' ref={chatBodyRef}>
              {isLoadingHistory ? (
                <div className='chat-loading'>
                  <div className='spinner-border text-success' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  <p>Loading chat history...</p>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className='chat-empty'>
                  <i className='fab fa-whatsapp'></i>
                  <p>No messages yet</p>
                  <small>Start a conversation!</small>
                </div>
              ) : (
                <div className='chat-messages'>
                  {chatMessages.map((msg, index) => {
                    const currentDate = getDateLabel(msg.created_at || msg.createddate);
                    const prevMsg = chatMessages[index - 1];
                    const prevDate = prevMsg
                      ? getDateLabel(prevMsg.created_at || prevMsg.createddate)
                      : null;
                    const showDate = currentDate !== prevDate;
                    const msgType =
                      msg.status?.toLowerCase() === 'outgoing' ||
                      msg.status?.toLowerCase() === 'sent'
                        ? 'sent'
                        : 'received';

                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className='wa-date-separator'>
                            <span>{currentDate}</span>
                          </div>
                        )}
                        <div className={`message-wrapper ${msgType}`}>
                          <div
                            className={`message-bubble ${
                              msg.delivery_status === 'failed' ? 'failed' : ''
                            }`}
                          >
                            <div className='wa-message-text'>
                              {renderMessageContent(msg)}
                            </div>
                            {msg.delivery_status === 'failed' &&
                              msg.err_messaage && (
                                <div className='message-error'>
                                  <small className='text-danger'>
                                    {msg.err_messaage}
                                  </small>
                                </div>
                              )}

                            <div className='wa-message-time'>
                              <span>
                                {formatTime(msg.created_at || msg.createddate)}
                              </span>
                              {msgType === 'sent' && (
                                <>
                                  {msg.delivery_status === 'sent' && (
                                    <i className='fa-solid fa-check text-secondary'></i>
                                  )}
                                  {msg.delivery_status === 'delivered' && (
                                    <i className='fa-solid fa-check-double text-secondary'></i>
                                  )}
                                  {msg.delivery_status === 'read' && (
                                    <i className='fa-solid fa-check-double text-primary wa-read'></i>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Message Input Area */}
            <div className='watconnect-chat-input'>
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type='file'
                accept='.txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf'
                onChange={handleFileSelect}
                className='d-none'
                id='file-upload'
              />
              <input
                ref={imageInputRef}
                type='file'
                accept='image/jpeg,image/png'
                onChange={handleImageSelect}
                className='d-none'
                id='image-upload'
              />
              <input
                ref={videoInputRef}
                type='file'
                accept='video/3gpp,video/mp4'
                onChange={handleVideoSelect}
                className='d-none'
                id='video-upload'
              />
              <input
                ref={audioInputRef}
                type='file'
                accept='audio/aac,audio/amr,audio/mpeg,audio/mp4,audio/ogg'
                onChange={handleAudioSelect}
                className='d-none'
                id='audio-upload'
              />

              {/* Attachment Preview */}
              {(selectedFile ||
                selectedImage ||
                selectedVideo ||
                selectedAudio) && (
                <div className='attachment-preview'>
                  <div className='attachment-info'>
                    <i
                      className={`fas fa-${selectedImage ? 'image' : selectedVideo ? 'video' : selectedAudio ? 'music' : 'file-alt'} me-2`}
                    ></i>
                    <span>
                      {
                        (
                          selectedFile ||
                          selectedImage ||
                          selectedVideo ||
                          selectedAudio
                        )?.name
                      }
                    </span>
                    <span className='attachment-size'>
                      (
                      {(
                        (
                          selectedFile ||
                          selectedImage ||
                          selectedVideo ||
                          selectedAudio
                        )?.size / 1024
                      ).toFixed(2)}{' '}
                      KB)
                    </span>
                  </div>
                  <button
                    className='remove-attachment-btn'
                    onClick={handleRemoveAttachment}
                    title='Remove attachment'
                  >
                    <i className='fas fa-times'></i>
                  </button>
                </div>
              )}

              {/* Attach Menu */}
              {showAttachMenu && !isRecording && (
                <div className='attach-menu'>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      imageInputRef.current?.click();
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-image'></i>
                    <span>Image</span>
                  </button>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      fileInputRef.current?.click();
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-file'></i>
                    <span>File</span>
                  </button>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      videoInputRef.current?.click();
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-video'></i>
                    <span>Video</span>
                  </button>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      audioInputRef.current?.click();
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-music'></i>
                    <span>Audio</span>
                  </button>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      startRecording();
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-microphone'></i>
                    <span>Voice</span>
                  </button>
                  <button
                    className='attach-menu-item'
                    onClick={() => {
                      setShowTemplateModal(true);
                      setShowAttachMenu(false);
                    }}
                  >
                    <i className='fas fa-clipboard-list'></i>
                    <span>Template</span>
                  </button>
                </div>
              )}

              {/* Input Container */}
              <div className='input-container'>
                {isRecording || audioBlob ? (
                  /* Recording UI */
                  <div className='recording-container'>
                    <button
                      className='cancel-recording-btn'
                      onClick={isRecording ? cancelRecording : deleteRecording}
                      title={isRecording ? 'Cancel' : 'Delete'}
                    >
                      <i className='fas fa-trash'></i>
                    </button>

                    <div className='recording-info'>
                      {isRecording ? (
                        <>
                          <div className='recording-pulse'>
                            <i className='fas fa-microphone'></i>
                          </div>
                          <span className='recording-duration'>
                            {formatDuration(recordingDuration)}
                          </span>
                        </>
                      ) : (
                        <>
                          <button
                            className='play-pause-btn'
                            onClick={togglePlayPause}
                            title={isPlaying ? 'Pause' : 'Play'}
                          >
                            <i
                              className={`fas fa-${isPlaying ? 'pause' : 'play'}`}
                            ></i>
                          </button>
                          <div className='voice-waveform-preview'>
                            <div className='waveform-bars'>
                              <span></span>
                              <span></span>
                              <span></span>
                              <span></span>
                              <span></span>
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                            <span className='recording-duration'>
                              {formatDuration(recordingDuration)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {isRecording ? (
                      <button
                        className='stop-recording-btn'
                        onClick={stopRecording}
                        title='Stop Recording'
                      >
                        <i className='fas fa-stop'></i>
                      </button>
                    ) : (
                      <button
                        className='send-btn'
                        onClick={sendVoiceMessage}
                        title='Send Voice Message'
                        disabled={isSending}
                      >
                        {isSending ? (
                          <div
                            className='spinner-border spinner-border-sm'
                            role='status'
                          >
                            <span className='visually-hidden'>Sending...</span>
                          </div>
                        ) : (
                          <i className='fas fa-paper-plane'></i>
                        )}
                      </button>
                    )}
                  </div>
                ) : (
                  /* Normal Input UI */
                  <>
                    <button
                      className='attach-icon-btn'
                      onClick={() => setShowFileAttachment(!showFileAttachment)}
                      title='Attach'
                    >
                      <i className='fas fa-paperclip'></i>
                    </button>

                    <Form.Control
                      as='textarea'
                      rows={1}
                      placeholder={
                        selectedFile ||
                        selectedImage ||
                        selectedVideo ||
                        selectedAudio
                          ? 'Add a caption for your media...'
                          : 'Type a message...'
                      }
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className='message-input'
                    />

                    <button
                      className='send-btn'
                      onClick={handleSendMessage}
                      disabled={
                        (!message.trim() &&
                          !selectedFile &&
                          !selectedImage &&
                          !selectedVideo &&
                          !selectedAudio) ||
                        isSending
                      }
                    >
                      {isSending ? (
                        <div
                          className='spinner-border spinner-border-sm'
                          role='status'
                        >
                          <span className='visually-hidden'>Sending...</span>
                        </div>
                      ) : (
                        <i className='fas fa-paper-plane'></i>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>

            {showFileAttachment && (
              <FileAttachmentModal
                isOpen={showFileAttachment}
                onClose={() => setShowFileAttachment(false)}
                recipientPhone={recipientPhone}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
                recipientName={recipientName}
                onSendComplete={handleFileSendComplete}
              />
            )}
          </>
        )}
      </div>

      {/* Template Modal - rendered outside chat container */}
      {showTemplateModal && (
        <TemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          recipientPhone={recipientPhone}
          recipientName={recipientName}
          selectedWhatsAppSetting={selectedWhatsAppSetting}
          onSendComplete={handleTemplateSend}
        />
      )}

      {/* Image Preview Modal - rendered outside chat container */}
      <ImagePreviewModal
        previewImage={previewImage}
        setPreviewImage={setPreviewImage}
      />
    </>
  );
};

// Image Preview Modal - rendered outside the chat container
const ImagePreviewModal = ({ previewImage, setPreviewImage }) => {
  if (!previewImage) return null;

  return (
    <div className='image-preview-modal' onClick={() => setPreviewImage(null)}>
      <div className='image-preview-content' onClick={e => e.stopPropagation()}>
        <button
          className='image-preview-close'
          onClick={() => setPreviewImage(null)}
        >
          <i className='fas fa-times'></i>
        </button>
        <img
          src={previewImage.src}
          alt={previewImage.alt}
          className='image-preview-img'
        />
        <div className='image-preview-info'>
          <span className='image-preview-name'>{previewImage.alt}</span>
        </div>
      </div>
    </div>
  );
};

export default WatConnectChat;
